import React, { useState, useEffect, useContext } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
  withStyles,
  Card,
  ListItem,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
} from "@material-ui/icons";
import { useParams } from "react-router-dom";
import { Instance } from "../../../Config";
import OwnerHeader from "../Components/OwnerHeader";
import GlobalContext from "../../../Context/globalContext";
import { SHOW_SNACKBAR } from "../../../Context/globalActions.types";
import { Skeleton } from "@material-ui/lab";

const useStyles = makeStyles((theme) => ({
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  tableContainer: {
    maxHeight: 600,
  },
}));

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: "#3f51b5",
    color: theme.palette.common.white,
    fontWeight: 600,
  },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
  root: {
    "&:nth-of-type(even)": {
      backgroundColor: theme.palette.action.hover,
    },
    "&:hover": {
      backgroundColor: "#F3F3F3",
      transform: "scale(1.0019)",
    },
  },
}))(TableRow);

const StaticTranslations = () => {
  const { mobileNo } = useParams();
  const { dispatch } = useContext(GlobalContext);
  const [languages, setLanguages] = useState([]);
  const [selectedLang, setSelectedLang] = useState("");
  const [translations, setTranslations] = useState([]);
  const [filteredTranslations, setFilteredTranslations] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [openDialog, setOpenDialog] = useState(false);
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteTransId, setDeleteTransId] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [editingRow, setEditingRow] = useState(null);

  // Form for new/edit translation
  const [translationForm, setTranslationForm] = useState({
    trans_key: "",
    trans_value: "",
    category: "ui_labels",
  });

  useEffect(() => {
    fetchLanguages();
  }, []);

  useEffect(() => {
    if (selectedLang) {
      fetchTranslations();
    }
  }, [selectedLang]);

  useEffect(() => {
    if (categoryFilter === "all") {
      setFilteredTranslations(translations);
    } else {
      setFilteredTranslations(
        translations.filter((t) => t.category === categoryFilter)
      );
    }
  }, [categoryFilter, translations]);

  const fetchLanguages = async () => {
    setDataLoading(true);
    try {
      const response = await Instance.get(`/languages?mobileNo=${mobileNo}`);
      setLanguages(response.data.result);
      if (response.data.result.length > 0) {
        const defaultLang = response.data.result.find((l) => l.is_default);
        setSelectedLang(
          defaultLang ? defaultLang.lang_code : response.data[0].lang_code
        );
      }
    } catch (error) {
      console.error("Error fetching languages:", error);
      dispatch({
        type: SHOW_SNACKBAR,
        payload: {
          message: "Error fetching languages",
          type: "error",
        },
      });
    } finally {
      setDataLoading(false);
    }
  };

  const fetchTranslations = async () => {
    setDataLoading(true);
    try {
      const response = await Instance.get(
        `/translations/static/list/${selectedLang}?mobileNo=${mobileNo}`
      );
      setTranslations(response.data);
    } catch (error) {
      console.error("Error fetching translations:", error);
      dispatch({
        type: SHOW_SNACKBAR,
        payload: {
          message: "Error fetching translations",
          type: "error",
        },
      });
    } finally {
      setDataLoading(false);
    }
  };

  const handleSaveTranslation = async () => {
    setLoading(true);
    try {
      await Instance.post(`/translations/static`, {
        lang_code: selectedLang,
        ...translationForm,
      });
      setOpenDialog(false);
      setEditingRow(null);
      setOpenSuccessDialog(true);
      fetchTranslations();
      setTranslationForm({
        trans_key: "",
        trans_value: "",
        category: "ui_labels",
      });
    } catch (error) {
      console.error("Error saving translation:", error);
      setErrorMessage(
        error.response?.data?.message || "Error saving translation"
      );
      setOpenErrorDialog(true);
    } finally {
      setLoading(false);
    }
  };

  const handleEditInline = (translation) => {
    setEditingRow(translation.trans_id);
    setTranslationForm({
      trans_key: translation.trans_key,
      trans_value: translation.trans_value,
      category: translation.category,
    });
  };

  const handleCancelEdit = () => {
    setEditingRow(null);
    setTranslationForm({
      trans_key: "",
      trans_value: "",
      category: "ui_labels",
    });
  };

  const handleAddNew = () => {
    setTranslationForm({
      trans_key: "",
      trans_value: "",
      category: "ui_labels",
    });
    setOpenDialog(true);
  };

  const handleDeleteClick = (transId) => {
    setDeleteTransId(transId);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    setLoading(true);
    try {
      await Instance.delete(
        `/translations/static/${deleteTransId}?mobileNo=${mobileNo}`
      );
      setOpenDeleteDialog(false);
      setDeleteTransId(null);
      fetchTranslations();
      dispatch({
        type: SHOW_SNACKBAR,
        payload: {
          message: "Translation deleted successfully",
          type: "success",
        },
      });
    } catch (error) {
      console.error("Error deleting translation:", error);
      setErrorMessage(
        error.response?.data?.message || "Error deleting translation"
      );
      setOpenErrorDialog(true);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { value: "ui_labels", label: "UI Labels" },
    { value: "errors", label: "Error Messages" },
    { value: "validations", label: "Validations" },
    { value: "notifications", label: "Notifications" },
    { value: "messages", label: "Messages" },
  ];

  return (
    <>
      <OwnerHeader>
        <div className="md:flex items-center justify-between px-3 md:px-4 py-3">
          <div>
            <div className="text-indigo-500 font-semibold text-sm md:text-base">
              Translation Management
            </div>
            <div className="text-xl md:text-2xl font-extrabold leading-7 sm:leading-10 truncate mt-1">
              Static Translations (UI Labels)
            </div>
          </div>
          <div className="flex items-center mb-3 mt-2 md:mt-0">
            <Button
              variant="contained"
              color="primary"
              disableElevation
              startIcon={<AddIcon />}
              onClick={handleAddNew}
              disabled={!selectedLang}
              className="bg-[#3333cc]"
            >
              Add Translation
            </Button>
          </div>
        </div>

        <div className="py-1 min-h-screen bg-[#F1F5F9] px-2">
          <Card>
            <ListItem className="pb-0">
              <Typography
                variant="h6"
                className="text-indigo-500 uppercase font-bold tracking-wider text-xs"
              >
                Filters
              </Typography>
            </ListItem>
            <ListItem className="pt-0">
              <Typography
                variant="h6"
                className="text-gray-500 uppercase font-bold tracking-wider text-[9px]"
              >
                Select language and category to view and edit static
                translations
              </Typography>
            </ListItem>
          </Card>

          <div className="mx-2 md:flex items-end gap-4 mb-4">
            <FormControl
              variant="outlined"
              className="md:w-64 w-full"
              margin="normal"
            >
              <InputLabel>Language</InputLabel>
              <Select
                value={selectedLang}
                onChange={(e) => setSelectedLang(e.target.value)}
                label="Language"
                className="bg-white"
              >
                {languages.map((lang) => (
                  <MenuItem key={lang.lang_code} value={lang.lang_code}>
                    {lang.lang_name} ({lang.native_name})
                    {lang.is_default && " - Default"}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              variant="outlined"
              className="md:w-64 w-full"
              margin="normal"
            >
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                label="Category"
                className="bg-white"
              >
                <MenuItem value="all">All Categories</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              disableElevation
              className="mb-2 bg-[#3333cc] text-white h-[56px]"
              onClick={fetchTranslations}
              disabled={!selectedLang}
            >
              Apply
            </Button>
          </div>

          <React.Fragment>
            <TableContainer
              style={{
                overflowX: "clip",
              }}
              component={Paper}
            >
              <div className="md:flex justify-between w-full my-3 px-2 pt-3">
                <Typography className="md:ml-2 text-md md:text-lg mb-2 font-medium">
                  Static Translations
                </Typography>
              </div>

              <div className="max-h-screen overflow-x-hidden">
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Translation Key</StyledTableCell>
                      <StyledTableCell>Category</StyledTableCell>
                      <StyledTableCell>Translated Value</StyledTableCell>
                      <StyledTableCell>Actions</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dataLoading && filteredTranslations.length === 0 ? (
                      Array.from(new Array(5)).map((_, index) => (
                        <StyledTableRow key={index}>
                          <StyledTableCell>
                            <Skeleton />
                          </StyledTableCell>
                          <StyledTableCell>
                            <Skeleton />
                          </StyledTableCell>
                          <StyledTableCell>
                            <Skeleton />
                          </StyledTableCell>
                          <StyledTableCell>
                            <Skeleton />
                          </StyledTableCell>
                        </StyledTableRow>
                      ))
                    ) : !dataLoading && filteredTranslations.length === 0 ? (
                      <StyledTableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="body2" color="textSecondary">
                            No translations found. Click "Add Translation" to
                            create one.
                          </Typography>
                        </TableCell>
                      </StyledTableRow>
                    ) : (
                      filteredTranslations.map((trans) => (
                        <StyledTableRow key={trans.trans_id}>
                          <TableCell>
                            {editingRow === trans.trans_id ? (
                              <TextField
                                fullWidth
                                value={translationForm.trans_key}
                                onChange={(e) =>
                                  setTranslationForm({
                                    ...translationForm,
                                    trans_key: e.target.value,
                                  })
                                }
                              />
                            ) : (
                              <code>{trans.trans_key}</code>
                            )}
                          </TableCell>
                          <TableCell>
                            {editingRow === trans.trans_id ? (
                              <Select
                                value={translationForm.category}
                                onChange={(e) =>
                                  setTranslationForm({
                                    ...translationForm,
                                    category: e.target.value,
                                  })
                                }
                                fullWidth
                              >
                                {categories.map((cat) => (
                                  <MenuItem key={cat.value} value={cat.value}>
                                    {cat.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            ) : (
                              <Chip
                                label={
                                  categories.find(
                                    (c) => c.value === trans.category
                                  )?.label || trans.category
                                }
                                size="small"
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            {editingRow === trans.trans_id ? (
                              <TextField
                                fullWidth
                                multiline
                                rows={2}
                                value={translationForm.trans_value}
                                onChange={(e) =>
                                  setTranslationForm({
                                    ...translationForm,
                                    trans_value: e.target.value,
                                  })
                                }
                              />
                            ) : (
                              trans.trans_value
                            )}
                          </TableCell>
                          <TableCell>
                            {editingRow === trans.trans_id ? (
                              <>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={handleSaveTranslation}
                                  disabled={loading}
                                  title="Save"
                                >
                                  <CheckIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={handleCancelEdit}
                                  disabled={loading}
                                  title="Cancel"
                                >
                                  <CancelIcon />
                                </IconButton>
                              </>
                            ) : (
                              <>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditInline(trans)}
                                  title="Edit"
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  onClick={() =>
                                    handleDeleteClick(trans.trans_id)
                                  }
                                  title="Delete"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </>
                            )}
                          </TableCell>
                        </StyledTableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </TableContainer>
          </React.Fragment>

          {/* Add Translation Dialog */}
          <Dialog
            open={openDialog}
            onClose={() => setOpenDialog(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Add New Translation</DialogTitle>
            <DialogContent>
              <TextField
                label="Translation Key"
                fullWidth
                margin="normal"
                variant="outlined"
                value={translationForm.trans_key}
                onChange={(e) =>
                  setTranslationForm({
                    ...translationForm,
                    trans_key: e.target.value,
                  })
                }
                placeholder="e.g., lbl_home, btn_submit"
                helperText="Use lowercase with underscores (snake_case)"
              />

              <FormControl fullWidth margin="normal" variant="outlined">
                <InputLabel>Category</InputLabel>
                <Select
                  value={translationForm.category}
                  onChange={(e) =>
                    setTranslationForm({
                      ...translationForm,
                      category: e.target.value,
                    })
                  }
                  label="Category"
                >
                  {categories.map((cat) => (
                    <MenuItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Translated Value"
                fullWidth
                margin="normal"
                variant="outlined"
                multiline
                rows={3}
                value={translationForm.trans_value}
                onChange={(e) =>
                  setTranslationForm({
                    ...translationForm,
                    trans_value: e.target.value,
                  })
                }
                placeholder={`Enter translation in ${
                  languages.find((l) => l.lang_code === selectedLang)
                    ?.lang_name || ""
                }`}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenDialog(false)} color="default">
                Cancel
              </Button>
              <Button
                onClick={handleSaveTranslation}
                color="primary"
                variant="contained"
                disableElevation
                disabled={
                  !translationForm.trans_key ||
                  !translationForm.trans_value ||
                  loading
                }
                className="bg-[#3333cc]"
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} className="mr-2" /> Saving...
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Success Dialog */}
          <Dialog
            open={openSuccessDialog}
            onClose={() => setOpenSuccessDialog(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Success</DialogTitle>
            <DialogContent>
              <Typography>Translation saved successfully!</Typography>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setOpenSuccessDialog(false)}
                color="primary"
                variant="contained"
                disableElevation
                className="bg-[#3333cc]"
              >
                OK
              </Button>
            </DialogActions>
          </Dialog>

          {/* Error Dialog */}
          <Dialog
            open={openErrorDialog}
            onClose={() => setOpenErrorDialog(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Error</DialogTitle>
            <DialogContent>
              <Typography>{errorMessage}</Typography>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setOpenErrorDialog(false)}
                color="secondary"
                variant="contained"
                disableElevation
              >
                OK
              </Button>
            </DialogActions>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog
            open={openDeleteDialog}
            onClose={() => setOpenDeleteDialog(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogContent>
              <Typography>
                Are you sure you want to delete this translation? This action
                cannot be undone.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setOpenDeleteDialog(false)}
                color="default"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteConfirm}
                color="secondary"
                variant="contained"
                disableElevation
                disabled={loading}
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} className="mr-2" /> Deleting...
                  </>
                ) : (
                  "Delete"
                )}
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      </OwnerHeader>
    </>
  );
};

export default StaticTranslations;
